import React, { useRef, useEffect, useState } from "react"
import { useAccessContext } from "@/contexts/AccessContext"
import ErrorIcon from "@mui/icons-material/Error"
import ReactDOMServer from "react-dom/server"
import { useMap } from "react-leaflet"
import GeoJsonLayer from "@/components/map/GeoJsonLayer"
import { useGisSuppressed } from "@/features/gis/GisInteractivityContext.jsx"
import { polygonLabelPoint } from "@/utils/polygonLabelPoint"
import L from "leaflet"
import "leaflet.markercluster"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { coreGet } from "@/services/coreApi"
/**
 * Filters GeoJSON features to only those visible within the current map viewport.
 * @param {import("leaflet").Map} map - Leaflet map instance to determine current bounds.
 * @param {Array<import("geojson").Feature>} features - Array of GeoJSON features to filter.
 * @returns {Array<import("geojson").Feature>} Filtered features that intersect the viewport.
 */
function filterViewportBoundingbox(map, features) {
  const bounds = map.getBounds()
  const filteredFeatures = features.filter((feature) => {
    const geom = feature.geometry
    // Determine visibility based on geometry type:
    // LineString: check if any segment point is within bounds
    if (geom.type === "LineString") {
      return geom.coordinates.some((coord) => bounds.contains([coord[1], coord[0]]))
      // MultiLineString: check each line segment
    } else if (geom.type === "MultiLineString") {
      return geom.coordinates.some((line) => line.some((coord) => bounds.contains([coord[1], coord[0]])))
      // Point: check if the point is within bounds
    } else if (geom.type === "Point") {
      return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
      // Polygon: check each ring coordinate
    } else if (geom.type === "Polygon") {
      return geom.coordinates.some((ring) => ring.some((coord) => bounds.contains([coord[1], coord[0]])))
      // Fallback: handle other geometry types generically
    } else {
      if (Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return bounds.contains([geom.coordinates[1], geom.coordinates[0]])
      }
      return false
    }
  })
  return filteredFeatures
}

/**
 * Decode a single condition AST node into a human-readable phrase.
 * Supports logical operators (and, or, not), comparison (eq, neq, gt, lt, ge, le),
 * and the contains operator with special handling for arrival/departure.
 * @param {Object} cond - Single condition AST object.
 * @returns {string} Human-readable condition.
 */
function decodeCondition(cond) {
  const op = Object.keys(cond)[0]
  const val = cond[op]
  // Logical AND / OR: join sub-conditions with 'and'/'or'
  if (op === "and" || op === "or") {
    const parts = val.map((sub) => decodeCondition(sub))
    const joiner = op === "and" ? " and " : " or "
    return parts.join(joiner)
  }
  // Logical NOT: negate a sub-condition
  if (op === "not") {
    return `not (${decodeCondition(val)})`
  }
  // Comparison operators: map to natural language
  const wordMap = {
    eq: "is equal to",
    neq: "is not equal to",
    gt: "is greater than",
    lt: "is less than",
    ge: "is greater than or equal to",
    le: "is less than or equal to",
  }
  if (wordMap[op]) {
    const [field, value] = val
    let displayValue = value
    if (field === "length" || field === "beam" || field === "maxdraft") {
      displayValue = `${value}m`
    } else if (field === "grt") {
      displayValue = `${value}t`
    }
    return `${field} ${wordMap[op]} ${displayValue}`
  }
  // Contains operator: check membership, with special region ID wording
  if (op === "contains") {
    const [field, value] = val
    if (field === "arrival" || field === "departure") {
      return `${field} includes region ID ${value}`
    }

    return `${field} includes ${value}`
  }
  // Fallback: stringify unknown condition
  return JSON.stringify(cond)
}

/**
 * Decode a parse_tree body node into a human-readable restriction description.
 * Handles direct return values (speed range, draft, leg usability) and conditionals.
 * @param {Object} node - AST node representing a restriction rule.
 * @returns {string} Description of the restriction.
 */
function decodeBody(node) {
  // Direct returns: extract speed, draft, or leg usability
  if (node.return) {
    const { minspeed, maxspeed, legUsable, allowedDraft } = node.return
    if (minspeed != null && maxspeed != null) {
      return `Speed: ${minspeed}-${maxspeed} knots`
    }
    if (minspeed != null) {
      return `Min speed: ${minspeed} knots`
    }
    if (maxspeed != null) {
      return `Max speed: ${maxspeed} knots`
    }
    if (legUsable != null) {
      return legUsable ? "Leg usable" : "Leg not usable"
    }
    if (allowedDraft != null) {
      return `Allowed draft: ${allowedDraft} m`
    }
    return JSON.stringify(node.return)
  }
  // Conditional rules: describe 'if-then-else' logic in plain English
  if (node.if) {
    const [cond, thenArr, elseArr] = node.if
    const condHuman = decodeCondition(cond)

    // Safely extract return objects from then/else arraysn
    const thenNode = Array.isArray(thenArr) && thenArr[0] && thenArr[0].return ? thenArr[0].return : null
    const elseNode = Array.isArray(elseArr) && elseArr[0] && elseArr[0].return ? elseArr[0].return : null

    const describeReturn = (ret) => {
      if (!ret || typeof ret !== "object") return "no explicit action"
      if (ret.maxspeed != null) return `a maximum speed of ${ret.maxspeed} knots`
      if (ret.minspeed != null) return `a minimum speed of ${ret.minspeed} knots`
      if (ret.legUsable != null) return ret.legUsable ? "leg usable" : "leg not usable"
      if (ret.allowedDraft != null) return `an allowed draft of ${ret.allowedDraft} metres`
      return "no explicit action"
    }

    const thenDesc = describeReturn(thenNode)
    const elseDesc = describeReturn(elseNode)

    return `If ${condHuman}, then ${thenDesc}${elseDesc ? `; otherwise, ${elseDesc}` : ""}.`
  }
  // Fallback: no recognizable rule
  return ""
}

export default function Restrictions({
  cwRestrictionsGeojson,
  setCwRestrictionsGeojson,
  toggleFlags,
  zoomLevel,
  dragged,
  resized,
  switchGisLayersActive,
}) {
  const { accessContext, setAccessContext } = useAccessContext()
  const map = useMap()
  const suppressed = useGisSuppressed()
  const tooltipCountRef = useRef(0)
  const markerRefs = useRef([])
  const clusterRef = useRef(null)
  const clearedThisCycleRef = useRef(false)
  const addedKeysRef = useRef(new Set())
  const [clusterVersion, setClusterVersion] = useState(0)
  const restrictionsEnabled =
    switchGisLayersActive && toggleFlags.includes("Restrictions")

  useEffect(() => {
    if (!restrictionsEnabled) return
    if (cwRestrictionsGeojson !== null) return

    let cancelled = false
    const load = async () => {
      try {
        const data = await coreGet(
          "/geo/restrictions",
          accessContext,
          setAccessContext,
          ".core /geo/restrictions error="
        )
        if (!cancelled) {
          setCwRestrictionsGeojson(data)
        }
      } catch (error) {
        console.error(error)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [
    accessContext,
    cwRestrictionsGeojson,
    restrictionsEnabled,
    setAccessContext,
    setCwRestrictionsGeojson,
  ])

  // Create/remove marker cluster when Restrictions or GIS toggle changes; clean up previous cluster
  useEffect(() => {
    if (clusterRef.current) {
      clusterRef.current.clearLayers()
      map.removeLayer(clusterRef.current)
      clusterRef.current = null
    }
    if (restrictionsEnabled) {
      // Disable clustering near max zoom to ensure individual ErrorIcon markers become visible when fully zoomed in
      clusterRef.current = L.markerClusterGroup({
        chunkedLoading: true,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        // Dynamischer Radius: wird mit jedem Zoom kleiner → Cluster lösen sich früher
        maxClusterRadius: (zoom) => Math.max(60 - (zoom - 6) * 6, 20),
        // Clustering schon ab Zoom ≥ 11 deaktivieren (früheres Auflösen)
        disableClusteringAtZoom: 11,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount()
          return L.divIcon({
            html: `<div style="
              background: rgba(255, 165, 0, 0.8); 
              color: black;
              border-radius: 50%;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 0 0 6px rgba(255, 165, 0, 0.25);
              font-weight: 500;
              font-size: 14px;
            ">${count}</div>`,
            className: "custom-cluster-icon",
            iconSize: [40, 40],
            iconAnchor: [20, 20],
          })
        },
      })
      clusterRef.current.addTo(map)
      setClusterVersion((v) => v + 1)
    }
    return () => {
      if (clusterRef.current) {
        clusterRef.current.clearLayers()
        map.removeLayer(clusterRef.current)
        clusterRef.current = null
      }
    }
  }, [map, restrictionsEnabled, toggleFlags, switchGisLayersActive])

  // Reset per-cycle flags whenever inputs change (forces fresh rebuild of cluster contents)
  useEffect(() => {
    clearedThisCycleRef.current = false
    addedKeysRef.current = new Set()
  }, [zoomLevel, dragged, resized, cwRestrictionsGeojson, toggleFlags, switchGisLayersActive])

  // Ensure cluster is removed when Restrictions toggle is off or GIS layers are globally disabled
  useEffect(() => {
    if (!restrictionsEnabled) {
      if (clusterRef.current) {
        clusterRef.current.clearLayers()
        map.removeLayer(clusterRef.current)
        clusterRef.current = null
      }
    }
  }, [map, restrictionsEnabled])

  // Remove rendered markers when Restrictions or GIS is turned off
  useEffect(() => {
    if (!restrictionsEnabled) {
      markerRefs.current.forEach((marker) => marker.remove())
      markerRefs.current = []
    }
  }, [restrictionsEnabled])

  // Release the restrictions dataset when the layer is not active.
  useEffect(() => {
    if (!restrictionsEnabled && cwRestrictionsGeojson !== null) {
      setCwRestrictionsGeojson(null)
    }
  }, [cwRestrictionsGeojson, restrictionsEnabled, setCwRestrictionsGeojson])

  return !restrictionsEnabled || cwRestrictionsGeojson === null ? null : (
    <GeoJsonLayer
      key={[zoomLevel, dragged, resized, clusterVersion, suppressed]}
      data={filterViewportBoundingbox(map, cwRestrictionsGeojson.features)}
      style={{ fillColor: "yellow", color: "orange", dashArray: "5,5", interactive: false }}
      onEachFeature={(feature, layer) => {
        if (clusterRef.current && !clearedThisCycleRef.current) {
          clusterRef.current.clearLayers()
          markerRefs.current = []
          clearedThisCycleRef.current = true
        }

        // Build a stable key per feature to avoid duplicate markers in cluster
        const getFeatureKey = (feat, ctr) => {
          // Prefer a stable id if provided by backend
          const fid = feat?.id || feat?.properties?.id || feat?.properties?.uuid || feat?.properties?.gid
          if (fid != null) return String(fid)
          // Fallback: geometry type + rounded center coords + info text
          if (ctr) {
            return `${feat?.geometry?.type || "geom"}:${ctr.lat.toFixed(6)},${ctr.lng.toFixed(6)}:${
              feat?.properties?.info || ""
            }`
          }
          return JSON.stringify(feat?.geometry || {})
        }

        const body = feature.properties.parse_tree?.body?.[0]
        const restrictionText = body ? decodeBody(body) : ""

        const popupContent = `<div>
          <strong>${feature.properties.info}</strong><br/>
          ${feature.properties.source_reference ? "Reference: " + feature.properties.source_reference + "<br/>" : ""}
          <br>
          ${restrictionText}<br/>
          ${feature.properties.note ? "note: " + feature.properties.note : ""}
        </div>`

        let center = null
        // Visuelles Zentrum (pole of inaccessibility) — liegt gut sichtbar im Polygon, Bbox-Center oft daneben
        const labelPt = polygonLabelPoint(feature)
        if (labelPt) {
          const [lng, lat] = labelPt
          center = L.latLng(lat, lng)
        }
        // Prefer point/marker latlng if available
        if (!center && typeof layer.getLatLng === "function") {
          const ll = layer.getLatLng()
          center = L.latLng(ll.lat, ll.lng)
        } else if (!center && typeof layer.getBounds === "function") {
          try {
            center = layer.getBounds().getCenter()
          } catch {}
        }
        // Fallback: derive from GeoJSON Point geometry
        if (!center && feature?.geometry?.type === "Point" && Array.isArray(feature.geometry.coordinates)) {
          const [lng, lat] = feature.geometry.coordinates
          center = L.latLng(lat, lng)
        }

        const fKey = getFeatureKey(feature, center)
        if (addedKeysRef.current.has(fKey)) {
          return
        }
        addedKeysRef.current.add(fKey)

        if (center && clusterRef.current) {
          // Render the ErrorIcon with an underlying white circle
          const iconHtml = ReactDOMServer.renderToStaticMarkup(
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                background: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <ErrorIcon style={{ color: "orange" }} />
            </div>
          )

          const customIcon = L.divIcon({
            html: iconHtml,
            className: "custom-error-icon",
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          })

          const marker = L.marker(center, { icon: customIcon, interactive: !suppressed })
          if (!suppressed) {
            marker.bindPopup(popupContent, { autoPan: false, maxHeight: 300, maxWidth: 320 })
          }
          clusterRef.current.addLayer(marker)
          markerRefs.current.push(marker)
        }
      }}
      transparent
    />
  )
}
